import React, {createContext, ReactElement, useContext, useEffect, useState} from "react";
import ngage from "services/ngage";
import {Navigate, useLocation} from "react-router-dom";
import {CurrentUser} from "types/User";
import {AuthResult} from "types/AuthResult";
import AuthCredentials from "types/AuthCredentials";
import client from "services/ngage/api-client";
import {AxiosError} from "axios";
import * as Sentry from "@sentry/react";
import FeatureFlag from "../types/FeatureFlag";
import {Permission} from "../types/Permissions";
import {useOperator} from "./OperatorContext";
import BrandedLoader from "../components/BrandedLoader";
import {useQuery, UseQueryResult} from "react-query";

// Types ----

type AuthContext = {
  isInitialised: boolean
  user: CurrentUser | null
  refetchCurrentUser: UseQueryResult<CurrentUser | null>["refetch"]
  roles: Array<string>
  permissions: Array<Permission>
  canUse: (feature: FeatureFlag) => boolean
  cannotUse: (feature: FeatureFlag) => boolean
  can: (permission: Permission) => boolean
  cannot: (permission: Permission) => boolean
  check: () => Promise<void>
  login: (credentials: AuthCredentials) => Promise<AuthResult>
  logout: () => Promise<void>
  twoFactorAuthChallenge: (code: string) => Promise<boolean>
  twoFactorAuthRecovery: (code: string) => Promise<boolean>
  clearUser: () => void
}

type AuthProviderProps = {
  children: React.ReactNode
}

// Context ----

const Context = createContext<AuthContext>(null!);

// Provider ----

/**
 * Context Provider
 */
export function AuthProvider({children}: AuthProviderProps) {
  const {isInitialised: isOperatorInitialised} = useOperator();

  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isInitialised, setIsInitialised] = useState(false);

  const { data: currentUser, refetch: refetchCurrentUser, isError: isCurrentUserError } = useQuery<CurrentUser | null>(
    ['users.current'],
    ngage.users.current,
    { enabled: isOperatorInitialised, retry: false }
  )

  useEffect(() => {
    if (user) {
      Sentry.setUser({...user, id: user.id.toString()})
    } else {
      const scope = Sentry.getCurrentScope();
      scope.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    // If we have an authenticated user set them in state and mark the auth context as initialised
    if (currentUser) {
      setUser(currentUser);
      setIsInitialised(true);
      return;
    }

    // If there was an error fetching the current user (e.g. because they're not authenticated), then we can also mark the auth context as initialised so that the app doesn't get stuck in a loading state
    if (isCurrentUserError) setIsInitialised(true);
  }, [isCurrentUserError, currentUser]);

  function canUse(feature: FeatureFlag): boolean {
    return user?.features[feature] === true;
  }

  function cannotUse(feature: FeatureFlag): boolean {
    return !canUse(feature);
  }

  function can(permission: Permission): boolean {
    return user?.permission_names.includes(permission) ?? false
  }

  function cannot(permission: Permission): boolean {
    return !can(permission);
  }

  function clearUser(): void {
    setUser(null)
  }

  async function check(): Promise<void> {
    if (user) {
      const currentUser = await ngage.users.current()

      if (!currentUser || currentUser.id !== user.id) {
        clearUser();
      }
    }
  }

  async function login(credentials: AuthCredentials): Promise<AuthResult> {
    return await ngage.auth.login(credentials);
  }

  async function logout(): Promise<void> {
    await ngage.auth.logout();
    clearUser();
  }

  async function twoFactorAuthChallenge(code: string) {
    return await ngage.auth.twoFactorChallenge(code);
  }

  async function twoFactorAuthRecovery(code: string) {
    return await ngage.auth.twoFactorRecovery(code);
  }

  // Register an interceptor to clear the user if the N-Gage API returns a 401 response
  useEffect(() => {
      client.interceptors.response.use(undefined, (error: AxiosError) => {
        if (error.response?.status === 401) {
          clearUser()
        }
        return Promise.reject(error)
      })
  }, []);

  // Context value
  let contextValue = {
    isInitialised,
    user,
    refetchCurrentUser,
    roles: user?.role_names ?? [],
    permissions: user?.permission_names ?? [],
    canUse,
    cannotUse,
    can,
    cannot,
    check,
    login,
    logout,
    twoFactorAuthChallenge,
    twoFactorAuthRecovery,
    clearUser,
  };

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

// Wrapper Component ----

/**
 * Wrapper component for protected routes
 */
export function RequireAuth({children}: { children: ReactElement }) {
  const auth = useAuth();
  const location = useLocation();

  const userHasOperatorAccess = auth.user?.permission_names.includes('operator-app:access');

  if (!auth.isInitialised) {
    return <BrandedLoader />
  }

  if (!auth.user || !userHasOperatorAccess) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they log in, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{from: location}} replace/>;
  }

  return children;
}

// Hook ----

/**
 * Custom hook
 */
export function useAuth() {
  return useContext(Context);
}